import { IconArrowRight, IconLink } from '@iwoca/icons';
import { Pill, Text } from '@iwoca/orion';
import { Card } from '@ui';
import { FormattedMessage } from 'react-intl';

import { BankIconWithDot } from '../OpenBankingStatusIndicator/BankIconWithDot';

type OpenBankingStatus = 'disconnected' | 'expired' | 'expiring' | 'connected';

type IconStatus = 'active' | 'expiring' | 'expired';

const PILL_VARIANT: Record<OpenBankingStatus, 'green' | 'yellow' | 'red'> = {
    disconnected: 'red',
    expired: 'red',
    expiring: 'yellow',
    connected: 'green'
};

const ICON_STATUS: Record<OpenBankingStatus, IconStatus> = {
    disconnected: 'expired',
    expired: 'expired',
    expiring: 'expiring',
    connected: 'active'
};

const PillLabel = ({ status }: { status: OpenBankingStatus }) => {
    switch (status) {
        case 'disconnected':
            return (
                <FormattedMessage
                    id="_.open_banking_status_card.pill.disconnected"
                    defaultMessage="Disconnected"
                />
            );
        case 'expired':
            return (
                <FormattedMessage
                    id="_.open_banking_status_card.pill.expired"
                    defaultMessage="Expired"
                />
            );
        case 'expiring':
            return (
                <FormattedMessage
                    id="_.open_banking_status_card.pill.expiring"
                    defaultMessage="Expiring"
                />
            );
        case 'connected':
            return (
                <FormattedMessage
                    id="_.open_banking_status_card.pill.connected"
                    defaultMessage="Connected"
                />
            );
    }
};

const Title = ({ status }: { status: OpenBankingStatus }) => {
    switch (status) {
        case 'disconnected':
            return (
                <FormattedMessage
                    id="_.open_banking_status_card.title.disconnected"
                    defaultMessage="You haven't linked your bank account"
                />
            );
        case 'expired':
            return (
                <FormattedMessage
                    id="_.open_banking_status_card.title.expired"
                    defaultMessage="Your open banking has expired"
                />
            );
        case 'expiring':
            return (
                <FormattedMessage
                    id="_.open_banking_status_card.title.expiring"
                    defaultMessage="Your open banking is about to expire"
                />
            );
        case 'connected':
            return (
                <FormattedMessage
                    id="_.open_banking_status_card.title.connected"
                    defaultMessage="Your bank account is connected"
                />
            );
    }
};

const Description = ({ status }: { status: OpenBankingStatus }) => {
    switch (status) {
        case 'disconnected':
            return (
                <FormattedMessage
                    id="_.open_banking_status_card.description.disconnected"
                    defaultMessage="Link your open banking to get the most up to date offers."
                />
            );
        case 'expired':
        case 'expiring':
            return (
                <FormattedMessage
                    id="_.open_banking_status_card.description.expiring"
                    defaultMessage="Relink your open banking to get the most up to date offers."
                />
            );
        case 'connected':
            return (
                <FormattedMessage
                    id="_.open_banking_status_card.description.connected"
                    defaultMessage="You are getting the most up to date offers."
                />
            );
    }
};

const ActionLabel = ({ status }: { status: OpenBankingStatus }) => {
    switch (status) {
        case 'connected':
            return (
                <FormattedMessage
                    id="_.open_banking_status_card.action_additional"
                    defaultMessage="Link an additional account"
                />
            );
        // disconnected / expired / expiring (the latter is currently suppressed
        // upstream in getOpenBankingStatus, but the branch is kept ready).
        case 'disconnected':
        case 'expired':
        case 'expiring':
            return (
                <FormattedMessage
                    id="_.open_banking_status_card.action"
                    defaultMessage="Link your bank account"
                />
            );
    }
};

export const OpenBankingStatusCard = ({
    status,
    onAction
}: {
    status: OpenBankingStatus;
    onAction: () => void;
}) => {
    return (
        <Card className="overflow-hidden !p-0">
            <button
                type="button"
                onClick={onAction}
                className="flex w-full cursor-pointer flex-col border-0 bg-transparent p-0 text-left"
            >
                <div className="flex gap-xl p-l">
                    <span className="flex size-[48px] shrink-0 items-center justify-center rounded-[6px] bg-secondary-95 p-[10px]">
                        <BankIconWithDot status={ICON_STATUS[status]} />
                    </span>
                    <div className="flex min-w-0 flex-1 flex-col gap-[10px]">
                        <Pill
                            variant={PILL_VARIANT[status]}
                            size="small"
                            className="self-start"
                        >
                            <PillLabel status={status} />
                        </Pill>
                        <Text className="text-s font-bold">
                            <Title status={status} />
                        </Text>
                        <Text className="font-light text-s">
                            <Description status={status} />
                        </Text>
                    </div>
                </div>
                <hr className="mx-l border-0 border-t border-solid border-secondary-90" />
                <div className="flex w-full items-center gap-s border-0 border-none bg-structure-100 p-l">
                    <IconLink fontSize={20} />
                    <Text className="flex-1 text-left text-s font-med leading-10">
                        <ActionLabel status={status} />
                    </Text>
                    <IconArrowRight fontSize={16} />
                </div>
            </button>
        </Card>
    );
};
